import { controlScoreAt, indexToSquare, parsePlacement } from '@chessmate/game-engine';

export type HeatTint = 'w' | 'b' | 'none';

export interface Heat {
  readonly tint: HeatTint;
  readonly opacity: number;
}

/** Control score → heat tint/opacity. Opacity ∝ |score| (clamped). */
export function heatForControl(score: number): Heat {
  if (score === 0) return { tint: 'none', opacity: 0 };
  const a = Math.min(1, Math.abs(score) / 10);
  return { tint: score > 0 ? 'w' : 'b', opacity: 0.15 + 0.6 * a };
}

/** Per-square control score for a fen, keyed by square (for overlays + tests). */
export function controlScores(fen: string): Record<string, number> {
  const placement = parsePlacement(fen);
  const scores: Record<string, number> = {};
  for (let i = 0; i < 64; i++) {
    scores[indexToSquare(i)] = controlScoreAt(placement, i);
  }
  return scores;
}

export interface Territory {
  readonly owner: 'w' | 'b';
  readonly squares: string[];
}

/** Flood-fill contiguous same-owner controlled squares into territories. */
export function computeTerritories(scores: Record<string, number>): Territory[] {
  const owned: Array<{ square: string; owner: 'w' | 'b' }> = [];
  for (const [square, score] of Object.entries(scores)) {
    if (score > 0) owned.push({ square, owner: 'w' });
    else if (score < 0) owned.push({ square, owner: 'b' });
  }
  const visited = new Set<string>();
  const territories: Territory[] = [];
  const dirs = [
    [0, 1], [0, -1], [1, 0], [-1, 0],
  ] as const;

  for (const start of owned) {
    if (visited.has(start.square)) continue;
    const stack = [start.square];
    const group: string[] = [];
    visited.add(start.square);
    while (stack.length > 0) {
      const sq = stack.pop();
      if (sq === undefined) break;
      group.push(sq);
      const f = sq.charCodeAt(0) - 97;
      const r = Number(sq[1]);
      for (const [df, dr] of dirs) {
        const nf = f + df;
        const nr = r + dr;
        if (nf < 0 || nf > 7 || nr < 1 || nr > 8) continue;
        const n = `${String.fromCharCode(97 + nf)}${nr}`;
        if (visited.has(n)) continue;
        const nScore = scores[n];
        if (nScore === undefined || nScore === 0) continue;
        const nOwner = nScore > 0 ? 'w' : 'b';
        if (nOwner !== start.owner) continue;
        visited.add(n);
        stack.push(n);
      }
    }
    territories.push({ owner: start.owner, squares: group });
  }
  return territories;
}
